import {pointerUtils, DAL} from '@wix/document-manager-core'
import {removePrefix} from '@wix/document-manager-utils'
import type {MetaData, Pointers, Pointer} from '@wix/document-services-types'
import {guidUtils} from '@wix/santa-core-utils'
import _ from 'lodash'
import {DATA_TYPES, DATA_TYPES_VALUES_WITH_HASH, DATA_TYPES_TO_INNER_NESTING} from '../constants/constants'
import * as jsonSchemas from '@wix/document-services-json-schemas'
import type {VariantsExtensionAPI} from '../extensions/variants/variants'
import type {AddItemOptions, DataModelExtensionAPI} from '../extensions/dataModel/dataModel'
import type {DataExtensionAPI} from '../extensions/data'

const {getPointer} = pointerUtils
const {getUniqueId} = guidUtils
const {
    namespaceMapping: {NAMESPACE_MAPPING, getNamespaceConfig}
} = jsonSchemas

const ITEM_TO_PREFIX_MAP: Record<string, string> = {
    data: 'dataItem',
    props: 'propItem',
    connections: 'connection',
    behaviors: 'behavior'
}
const DEFAULT_DATA_PREFIX = new Set(Object.keys(NAMESPACE_MAPPING))

const generateItemIdWithPrefix = (prefix: string, addPrefixDelimiter = true) =>
    getUniqueId(prefix, addPrefixDelimiter ? '-' : '', {bucket: `generateItemIdWithPrefix_${prefix}`})

const generateUniqueIdByType = (type: string, pageId: string, dal: DAL, pointers: Pointers) => {
    const prefix = ITEM_TO_PREFIX_MAP[type] ?? type
    let newId = generateItemIdWithPrefix(prefix)
    const retries = 3
    for (let i = 0; i < retries && dal.has(pointers.data.getItem(type, newId, pageId)); i++) {
        newId = generateItemIdWithPrefix(prefix)
    }

    if (dal.has(pointers.data.getDataItem(newId, pageId))) {
        throw new Error('Failed to generate unique Id')
    }

    return newId
}

/**
 * Generates a new data Item ID.
 * usually the path to the page to add the data to.
 */
const generateNewDataItemId = () => {
    return generateItemIdWithPrefix('dataItem')
}

/**
 * Generates a new property Item ID.
 * usually the path to the page to add the data to.
 */
const generateNewPropertiesItemId = () => {
    return generateItemIdWithPrefix('propItem')
}

/**
 * return the function to generate a new id
 *
 * @param {string} idType (data || props || style)
 */
const generateNewId = (idType: string) => {
    switch (idType) {
        case 'data':
            return generateNewDataItemId()
        case 'props':
        case 'properties':
            return generateNewPropertiesItemId()
        case 'design':
            return generateNewDataItemId()
        case 'behaviors':
            return generateItemIdWithPrefix('behavior')
        case 'connections':
            return generateItemIdWithPrefix('connection')
    }

    //general/default case:
    if (DEFAULT_DATA_PREFIX.has(idType)) {
        return generateItemIdWithPrefix(idType)
    }

    throw new Error(`Unknown idType: ${idType}`)
}

const BASE_METADATA = {schemaVersion: '1.0'}
const DEFAULT_METADATA = {...BASE_METADATA, isPreset: false, isHidden: false}
const createMetaData = (template: Record<string, string | boolean>, pageId?: string) =>
    pageId ? {...template, pageId} : {...template}

const GET_DEFAULT_META_DATA = {
    [DATA_TYPES.prop]: createMetaData.bind(null, BASE_METADATA),
    DEFAULT: createMetaData.bind(null, DEFAULT_METADATA),
    //Components should not have isPreset, isHidden, schemaVersion. They only have sig and pageId:
    DESKTOP: createMetaData.bind(null, {}),
    MOBILE: createMetaData.bind(null, {})
}

const createDefaultMetaData = (metaDataOverrides?: MetaData, pageId?: string, itemType: string = 'DEFAULT') => {
    const defaultMetaDataCreator = GET_DEFAULT_META_DATA[itemType] || GET_DEFAULT_META_DATA.DEFAULT

    return _.assign(defaultMetaDataCreator(pageId), metaDataOverrides)
}

const addDefaultMetaData = (newDataItem: {metaData?: MetaData}, pageId?: string, itemType: string = 'DEFAULT') => {
    newDataItem.metaData = createDefaultMetaData(newDataItem.metaData, pageId, itemType)
}

const shouldMergeDataItems = (existingDataItem: any, newDataItem: any) =>
    existingDataItem &&
    (!existingDataItem.type || !newDataItem.type || _.isEqual(existingDataItem.type, newDataItem.type))

const REF_PREFIX = '#'
const getIdFromRef = (ref: string) => removePrefix(ref, REF_PREFIX)

//this should be used only for root structure queries
const createRef = (id: string, namespace: string) =>
    DATA_TYPES_VALUES_WITH_HASH[namespace] && !id.startsWith(REF_PREFIX) ? `${REF_PREFIX}${id}` : id

const createInnerRef = (id: string) => (!id.startsWith(REF_PREFIX) ? `${REF_PREFIX}${id}` : id)

const removeDataByNamespace = (
    extensionAPI: any,
    componentPointer: Pointer,
    namespace: string,
    propsToRemove: string[],
    isBuilder: boolean,
    options?: AddItemOptions
): void => {
    const {data, variants, dataModel} = extensionAPI as DataExtensionAPI & VariantsExtensionAPI & DataModelExtensionAPI

    const config = getNamespaceConfig(namespace)

    const dataItem = config.supportsVariants
        ? variants.getItemConsideringVariantsForPointer(componentPointer, namespace)
        : dataModel.components.getItem(componentPointer, namespace)

    if (!dataItem) return

    const shouldBeNested = isBuilder && !!DATA_TYPES_TO_INNER_NESTING[namespace]

    let toUpdateData = {} as any

    if (shouldBeNested) {
        const nestingKey = DATA_TYPES_TO_INNER_NESTING[namespace]
        const currentData = dataItem?.[nestingKey]

        if (!currentData) return

        const filteredData = Object.fromEntries(
            Object.entries(currentData).filter(([key]) => !propsToRemove.includes(key))
        )
        toUpdateData = {[nestingKey]: filteredData}
    } else {
        toUpdateData = Object.fromEntries(propsToRemove.map(prop => [prop, null]))
        const itemType = data.getItemType(getPointer(dataItem._id, namespace))
        options = {...options, refTypes: [itemType!]}
    }

    if (config.supportsVariants) {
        variants.updateComponentDataConsideringVariants(componentPointer, toUpdateData, namespace)
    } else {
        dataModel.components.addItem(componentPointer, namespace, toUpdateData, undefined, options)
    }
}

export {
    shouldMergeDataItems,
    createDefaultMetaData,
    addDefaultMetaData,
    generateUniqueIdByType,
    getIdFromRef,
    generateItemIdWithPrefix,
    generateNewId,
    createRef,
    createInnerRef,
    removeDataByNamespace
}
